import React, { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { ReviewSubmissionModal } from './ReviewSubmissionModal';
import { useReviews } from '@/hooks/useReviews';

interface PostJobReviewFlowProps {
  jobId: string;
  jobStatus: string;
  organizationId: string;
  vendorUserId?: string;
  clientUserId?: string;
  onComplete?: () => void;
}

export const PostJobReviewFlow: React.FC<PostJobReviewFlowProps> = ({
  jobId,
  jobStatus,
  organizationId,
  vendorUserId,
  clientUserId,
  onComplete
}) => {
  const { user } = useAuth();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewerType, setReviewerType] = useState<'client' | 'vendor'>('client');
  const [revieweeUserId, setRevieweeUserId] = useState<string>('');
  const [hasDeclined, setHasDeclined] = useState(false);
  
  const { canUserReviewJob } = useReviews();

  // Check eligibility when component mounts or job status changes
  useEffect(() => {
    // Guard: Don't proceed if already declined or missing user
    if (hasDeclined || !user?.id) return;

    const checkEligibility = async () => {
      // Allow reviews for both accepted and closed jobs (vendor may not have reviewed before closure)
      const reviewableStatuses = ['accepted', 'closed'];
      if (!reviewableStatuses.includes(jobStatus) || !vendorUserId || !clientUserId) {
        return;
      }

      // Determine user type and reviewee
      let userType: 'client' | 'vendor';
      let revieweeId: string;
      
      if (clientUserId === user.id) {
        userType = 'client';
        revieweeId = vendorUserId;
      } else if (vendorUserId === user.id) {
        userType = 'vendor';
        revieweeId = clientUserId;
      } else {
        return;
      }

      const canReview = await canUserReviewJob(jobId, userType);

      if (canReview) {
        setReviewerType(userType);
        setRevieweeUserId(revieweeId);
        setShowReviewModal(true);
      }
    };

    // Add delay to ensure database state is consistent after job acceptance
    const timer = setTimeout(checkEligibility, 500);
    return () => clearTimeout(timer);
  }, [jobStatus, user?.id, jobId, vendorUserId, clientUserId, hasDeclined, canUserReviewJob]);

  const handleReviewSuccess = () => {
    setShowReviewModal(false);
    onComplete?.();
  };

  const handleSkip = () => {
    setShowReviewModal(false);
    setHasDeclined(true);
    onComplete?.();
  };

  if (!showReviewModal || !revieweeUserId) {
    return null;
  }

  return (
    <ReviewSubmissionModal
      open={showReviewModal}
      onOpenChange={handleSkip}
      jobId={jobId}
      revieweeUserId={revieweeUserId}
      reviewerType={reviewerType}
      organizationId={organizationId}
      onSuccess={handleReviewSuccess}
    />
  );
};